import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Building2, User, Music, Calendar, Link2, Image } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function EntityProfiles({ className = '' }: { className?: string }) {
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      const scrollTl = gsap.timeline({
        scrollTrigger: { trigger: section, start: 'top top', end: '+=70%', pin: true, scrub: 0.6 },
      });
      scrollTl.fromTo(headerRef.current, { y: 30, opacity: 0 }, { y: 0, opacity: 1, ease: 'none' }, 0);
      scrollTl.fromTo(cardsRef.current?.children || [], { y: 50, opacity: 0 }, { y: 0, opacity: 1, stagger: 0.03, ease: 'none' }, 0.05);
      scrollTl.fromTo(headerRef.current, { y: 0, opacity: 1 }, { y: '-10vh', opacity: 0, ease: 'power2.in' }, 0.7);
      scrollTl.fromTo(cardsRef.current?.children || [], { y: 0, opacity: 1 }, { y: '10vh', opacity: 0, stagger: 0.02, ease: 'power2.in' }, 0.72);
    }, section);

    return () => ctx.revert();
  }, []);

  const entities = [
    { type: 'venue', name: 'Golden Nugget', icon: Building2, assets: 12, events: 8, color: '#FF6B4A' },
    { type: 'artist', name: 'OpticMystic', icon: User, assets: 6, events: 15, color: '#2EC4B6' },
    { type: 'artist', name: 'Snowden', icon: Music, assets: 9, events: 22, color: '#2EC4B6' },
  ];

  return (
    <section ref={sectionRef} className={`pinned-section bg-[#122A2F] flex items-center ${className}`}>
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div ref={headerRef} className="text-center mb-10" style={{ willChange: 'transform, opacity' }}>
            <span className="font-mono text-xs uppercase tracking-[0.12em] text-[#2EC4B6] mb-4 block">Entity Profiles</span>
            <h2 className="font-display font-bold text-[clamp(36px,4.5vw,64px)] leading-tight text-white mb-4">
              Know your artists.<br />
              <span className="text-[#2EC4B6]">Know their assets.</span>
            </h2>
            <p className="text-lg text-[#A9B4B8] max-w-2xl mx-auto">
              Every artist and venue gets a profile with their logos, headshots, and brand elements—automatically referenced when generating flyers.
            </p>
          </div>
          <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {entities.map((entity, i) => (
              <div key={i} className="bg-[#0B0F10] rounded-xl p-6 border border-white/10 hover:border-[#2EC4B6]/30 transition-all group" style={{ willChange: 'transform, opacity' }}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${entity.color}20` }}>
                    <entity.icon size={28} style={{ color: entity.color }} />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-xl text-white">{entity.name}</h3>
                    <span className="text-sm text-[#A9B4B8] capitalize">{entity.type}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Image size={14} className="text-[#2EC4B6]" />
                      <span className="text-xs text-[#A9B4B8]">Assets</span>
                    </div>
                    <span className="text-xl font-semibold text-white">{entity.assets}</span>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar size={14} className="text-[#2EC4B6]" />
                      <span className="text-xs text-[#A9B4B8]">Events</span>
                    </div>
                    <span className="text-xl font-semibold text-white">{entity.events}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className="flex-1 px-3 py-2 bg-[#2EC4B6]/10 text-[#2EC4B6] text-sm rounded-lg hover:bg-[#2EC4B6]/20 transition-colors">View Assets</button>
                  <button className="px-3 py-2 bg-white/5 text-[#A9B4B8] rounded-lg hover:bg-white/10 transition-colors">
                    <Link2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
